import { t } from "@lingui/macro";
import {
  NavigationAction,
  NavigationProp,
  useNavigation,
  usePreventRemove,
} from "@react-navigation/native";
import { useResponsiveProp } from "@shopify/restyle";
import graphql from "babel-plugin-relay/macro";
import { format, parseISO } from "date-fns";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import {
  isAppliedPatronLoyaltyReward,
  PatronCheckoutLoyaltyRewards,
} from "@/features/Loyalty/components/PatronCheckoutLoyaltyRewards/PatronCheckoutLoyaltyRewards";
import { PopUpModal } from "@whoosh/design-system/src/components/overlay/PopUpModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { currentProfileAtom } from "../../../features/Club/atoms";
import { ClubStackNavigatorParamList } from "../../../navigators/ClubNavigator/ClubStackNavigator";
import { useFlags } from "../../../utils/launchDarkly";
import { useInitEventForm } from "../../EventDetail/atoms/useInitEventForm";
import { useInitEventMeta } from "../../EventDetail/atoms/useInitEventMeta";
import { useInitEventSignUps } from "../../EventDetail/atoms/useInitEventSignUps";
import { useInitGroupEvent } from "../../EventDetail/atoms/useInitGroupEvent";
import { UpdateAttendeesGroupEventsModal } from "../../EventDetail/components/UpdateAttendeesGroupEvents";
import { useDeleteEventSignUp } from "../../EventDetail/hooks/useDeleteEventSignUp";
import { getEventSignupCheckoutState } from "../hooks/useEventSignupCheckoutState";
import { EventSignupConfirmationModal } from "./EventSignupConfirmationModal";
import { EventSignupSummaryActions } from "./EventSignupSummaryActions";
import { EventSignupSummaryHeader } from "./EventSignupSummaryHeader";
import { EventSignupSummaryPaymentSummary } from "./EventSignupSummaryPaymentSummary";
import { EventSignupSummaryPlayerList } from "./EventSignupSummaryPlayerList";
import { EventSignupSummaryReservation } from "./EventSignupSummaryReservation";
import { EventSignupSummarySkeleton } from "./EventSignupSummarySkeleton";
import { EventSignupSummaryTotal } from "./EventSignupSummaryTotal";
import { EventSignupSummaryContent_event$key } from "./__generated__/EventSignupSummaryContent_event.graphql";
import { EventSignupSummaryContent_signUp$key } from "./__generated__/EventSignupSummaryContent_signUp.graphql";

type Props = {
  eventFragmentRef: EventSignupSummaryContent_event$key;
  signUpFragmentRef: EventSignupSummaryContent_signUp$key;
  handleGoBack: () => void;
};

const formatCancelDeadline = (iso: string): string => {
  try {
    return format(parseISO(iso), "MMM do 'at' h:mm a");
  } catch {
    return "";
  }
};

const getRegistrationPrice = (registration: unknown): number => {
  const node = registration as
    | {
        primaryChargeType?: {
          chargeRuleSet?: {
            chargeRules?: {
              edges?: ReadonlyArray<{
                node?: { price?: number | null } | null;
              } | null> | null;
            } | null;
          } | null;
        } | null;
      }
    | null
    | undefined;

  return (
    node?.primaryChargeType?.chargeRuleSet?.chargeRules?.edges?.[0]?.node
      ?.price ?? 0
  );
};

export const EventSignupSummaryContent = ({
  eventFragmentRef,
  signUpFragmentRef,
  handleGoBack,
}: Props) => {
  const event = useFragment(
    EventSignupSummaryContentEventFragment,
    eventFragmentRef
  );
  const signUp = useFragment(
    EventSignupSummaryContentSignUpFragment,
    signUpFragmentRef
  );

  const isMobile = useResponsiveProp({ base: 1, md: 0 });
  const isDraftSignUp = signUp.status === "DRAFT";
  const { eventPrePayment, patronLoyalty } = useFlags();
  const eventPrePaymentEnabled =
    eventPrePayment &&
    (event.eventSeries?.facility?.isEventPrePaymentEnabled ?? false);
  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [isLeaveConfirmed, setIsLeaveConfirmed] = useState(false);
  const pendingRemoveActionRef = useRef<NavigationAction | null>(null);
  const currentProfile = useAtomValue(currentProfileAtom);
  const memberId = currentProfile?.memberId;

  const allowLeave = useCallback(() => {
    setIsLeaveConfirmed(true);
  }, []);

  const [deleteEventSignUp, isDeletingEventSignUp] =
    useDeleteEventSignUp(allowLeave);

  // Intercept back gesture / hardware back / browser back while the signup is
  // still DRAFT so leave confirmation + draft deletion cannot be bypassed.
  usePreventRemove(
    isDraftSignUp && !!memberId && !isLeaveConfirmed,
    ({ data }) => {
      pendingRemoveActionRef.current = data.action;
      setIsLeaveModalOpen(true);
    }
  );

  useEffect(() => {
    if (!isLeaveConfirmed) {
      return;
    }

    const action = pendingRemoveActionRef.current;
    pendingRemoveActionRef.current = null;

    if (action) {
      navigation.dispatch(action);
      return;
    }

    handleGoBack();
  }, [handleGoBack, isLeaveConfirmed, navigation]);

  const handleClose = useCallback(() => {
    if (isDraftSignUp && memberId && !isLeaveConfirmed) {
      // Header X opens the leave modal directly. Back gesture / hardware back /
      // browser back go through usePreventRemove and store a pending action.
      pendingRemoveActionRef.current = null;
      setIsLeaveModalOpen(true);
      return;
    }
    handleGoBack();
  }, [handleGoBack, isDraftSignUp, isLeaveConfirmed, memberId]);

  const handleConfirmLeave = useCallback(() => {
    if (memberId) {
      deleteEventSignUp({ eventSignUpId: signUp.id }, memberId);
    }
  }, [deleteEventSignUp, memberId, signUp.id]);

  const handleCancelLeave = useCallback(() => {
    pendingRemoveActionRef.current = null;
    setIsLeaveModalOpen(false);
  }, []);

  useInitGroupEvent({ fragmentRef: event });
  useInitEventMeta({ fragmentRef: event });
  useInitEventForm({ fragmentRef: event });
  useInitEventSignUps({ fragmentRef: event });

  const hasRegistrations = (signUp.registrations?.edges?.length ?? 0) > 0;
  const isWaitlistSignUp = signUp.waitlistIndex !== null;
  const isPublicEvent =
    event.eventSeries.facility?.club?.supportsPublic ?? false;
  const registrationSubtotal =
    signUp.registrations?.edges?.reduce(
      (sum, edge) => sum + getRegistrationPrice(edge?.node),
      0
    ) ?? 0;
  const activeOrders =
    signUp.orders?.edges?.flatMap((edge) => (edge?.node ? [edge.node] : [])) ??
    [];
  const {
    loyaltyOrder: order,
    enrollmentOfferOrder,
    total: totalCharges,
  } = getEventSignupCheckoutState({
    orders: activeOrders,
    registrationSubtotal,
    isDraftSignUp,
  });
  const confirmedLoyaltyOrder = !isDraftSignUp ? activeOrders[0] ?? null : null;
  const loyaltyDisplayOrder = isDraftSignUp ? order : confirmedLoyaltyOrder;
  const loyaltyRewards = loyaltyDisplayOrder?.squareLoyaltyRewards ?? [];
  const appliedLoyaltyRewards = loyaltyRewards.filter((reward) =>
    isAppliedPatronLoyaltyReward(reward.status)
  );
  const hasCharges = totalCharges > 0;
  const shouldUsePaymentLayout = isDraftSignUp && isPublicEvent && hasCharges;
  const shouldShowDraftChargesSummary =
    isMobile && shouldUsePaymentLayout && !isWaitlistSignUp;
  const shouldShowConfirmedChargesSummary = !isDraftSignUp && hasCharges;
  const shouldShowPaymentSummary = !isMobile && shouldUsePaymentLayout;
  const shouldShowLoyaltyRewards =
    !isWaitlistSignUp &&
    eventPrePayment &&
    event.prePaymentRequirement === "FULL" &&
    (isDraftSignUp
      ? order !== null
      : confirmedLoyaltyOrder !== null && appliedLoyaltyRewards.length > 0);
  const shouldShowStandaloneLoyaltyRewards =
    shouldShowLoyaltyRewards && !shouldShowPaymentSummary;
  const shouldShowFooter =
    isDraftSignUp && (isMobile || !shouldUsePaymentLayout);
  const eventName = event.eventSeries.name;

  const cancelDeadlineAt = signUp.signUpWindow?.cancelDeadlineAt ?? null;
  const cancelDeadlineLabel = cancelDeadlineAt
    ? formatCancelDeadline(cancelDeadlineAt)
    : null;
  const canCancelSignUp = signUp.canDelete === true;

  const showConfirmationModal = useCallback(() => {
    setIsConfirmationModalOpen(true);
  }, []);

  const closeConfirmationModal = useCallback(() => {
    setIsConfirmationModalOpen(false);
  }, []);

  const goToEventPage = useCallback(() => {
    setIsConfirmationModalOpen(false);
    navigation.navigate("EventDetail", { eventID: event.id });
  }, [event.id, navigation]);

  const goHome = useCallback(() => {
    setIsConfirmationModalOpen(false);
    navigation.navigate("Home", { screen: "MainHome" });
  }, [navigation]);

  if (!hasRegistrations) {
    return (
      <Box flex={1}>
        <EventSignupSummaryHeader
          fragmentRef={event}
          handleGoBack={handleGoBack}
        />
        <EventSignupSummarySkeleton />
      </Box>
    );
  }

  const mainContent = (
    <Box
      flex={1}
      width="100%"
      backgroundColor="surface-primary"
      overflow="hidden"
    >
      <EventSignupSummaryHeader
        fragmentRef={event}
        handleGoBack={handleClose}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        bounces={false}
        contentContainerStyle={{
          paddingBottom: 109,
        }}
      >
        <VStack gap={isMobile ? 2 : 0}>
          <VStack gap={5} padding={5} backgroundColor="surface-primary">
            <EventSignupSummaryPlayerList
              eventFragmentRef={event}
              signUpFragmentRef={signUp}
              isPublicEvent={isPublicEvent}
            />

            <EventSignupSummaryActions
              eventFragmentRef={event}
              signUpFragmentRef={signUp}
            />

            {canCancelSignUp && cancelDeadlineLabel ? (
              <HStack justifyContent="center">
                <Box
                  backgroundColor="surface-secondary"
                  paddingHorizontal={4}
                  paddingVertical={2}
                  style={{ borderRadius: 100 }}
                >
                  <Text variant="mainSmallSemiBold">
                    {t`Cancel available until ${cancelDeadlineLabel}`}
                  </Text>
                </Box>
              </HStack>
            ) : null}
          </VStack>

          {shouldShowDraftChargesSummary ||
          shouldShowConfirmedChargesSummary ? (
            <EventSignupSummaryTotal
              fragmentRef={signUp}
              finalAmountLabel={
                shouldShowConfirmedChargesSummary
                  ? t`Total`
                  : eventPrePaymentEnabled &&
                    event.prePaymentRequirement === "COLLECT_CARD_ON_FILE"
                  ? t`Total due upon arrival`
                  : eventPrePaymentEnabled &&
                    event.prePaymentRequirement === "NONE"
                  ? t`Total`
                  : undefined
              }
            />
          ) : null}
          {shouldShowStandaloneLoyaltyRewards && loyaltyDisplayOrder ? (
            <Box padding={5} paddingTop={0}>
              <PatronCheckoutLoyaltyRewards
                analyticsProperties={{ eventSignUpId: signUp.id }}
                loyaltyRewards={
                  isDraftSignUp ? loyaltyRewards : appliedLoyaltyRewards
                }
                orderRef={loyaltyDisplayOrder}
                enrollmentOfferRef={
                  isDraftSignUp && patronLoyalty ? enrollmentOfferOrder : null
                }
                display={isDraftSignUp ? "all" : "rewards"}
                section={isDraftSignUp ? "all" : "applied"}
                editable={isDraftSignUp}
              />
            </Box>
          ) : null}
        </VStack>
      </ScrollView>

      {shouldShowFooter ? (
        <EventSignupSummaryReservation
          fragmentRef={signUp}
          onExpireCallback={allowLeave}
          onConfirmSuccess={showConfirmationModal}
        />
      ) : null}
    </Box>
  );

  return (
    <HStack width="100%" flex={1} backgroundColor="surface-secondary">
      {!isMobile ? (
        <Box
          backgroundColor={
            shouldUsePaymentLayout ? "surface-primary" : "surface-secondary"
          }
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}

      <Box
        backgroundColor={
          shouldUsePaymentLayout ? "surface-primary" : "surface-secondary"
        }
        flexShrink={1}
        flexGrow={1}
      >
        <Box
          maxWidth={isMobile ? "100%" : 544}
          marginLeft="auto"
          width="100%"
          height="100%"
          marginTop={isMobile ? 0 : 3}
          backgroundColor="surface-primary"
          marginRight={!shouldUsePaymentLayout ? "auto" : isMobile ? 0 : 3}
        >
          {mainContent}
        </Box>
      </Box>

      {shouldShowPaymentSummary ? (
        <EventSignupSummaryPaymentSummary
          fragmentRef={signUp}
          onExpireCallback={allowLeave}
          onConfirmSuccess={showConfirmationModal}
        />
      ) : null}

      {!isMobile ? (
        <Box
          backgroundColor="surface-secondary"
          flexGrow={1}
          flexShrink={1}
          flexBasis={0}
        />
      ) : null}

      <UpdateAttendeesGroupEventsModal
        eventId={event.id}
        fragmentRef={event}
        unknownGuestsAllowed={event.unknownGuestsAllowed ?? false}
      />
      <EventSignupConfirmationModal
        eventName={eventName}
        isOpen={isConfirmationModalOpen}
        onClose={closeConfirmationModal}
        onGoHome={goHome}
        onViewEventPage={goToEventPage}
      />
      <PopUpModal
        isOpen={isLeaveModalOpen}
        upperTitle={t`Leave Signup`}
        theme="danger"
        cancelText={t`Cancel`}
        confirmText={t`Leave`}
        isConfirmDisabled={isDeletingEventSignUp}
        onClose={(confirm: boolean) => {
          if (confirm) {
            handleConfirmLeave();
          } else {
            handleCancelLeave();
          }
        }}
        content={
          <VStack>
            <Text color="text-secondary">
              {t`Are you sure you want to leave this signup? All progress will be lost.`}
            </Text>
          </VStack>
        }
      />
    </HStack>
  );
};

export const EventSignupSummaryContentEventFragment = graphql`
  fragment EventSignupSummaryContent_event on Event {
    id
    unknownGuestsAllowed
    prePaymentRequirement
    eventSeries {
      name
      facility {
        isEventPrePaymentEnabled
        club {
          supportsPublic
        }
      }
    }
    ...EventSignupSummaryHeader_event
    ...EventSignupSummaryPlayerList_event
    ...EventSignupSummaryActions_event
    ...useInitGroupEvent_event
    ...useInitEventMeta_event
    ...useInitEventForm_formQuestionConfiguration
    ...useInitEventSignUps_event
    ...UpdateAttendeesGroupEventsModal_event
  }
`;

export const EventSignupSummaryContentSignUpFragment = graphql`
  fragment EventSignupSummaryContent_signUp on EventSignUp
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    id
    canDelete
    status
    waitlistIndex
    registrations(first: 50) {
      edges {
        node {
          id
          primaryChargeType {
            id
            chargeRuleSet {
              chargeRules(first: 1) {
                edges {
                  node {
                    id
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
    orders(active: true) {
      edges {
        node {
          id
          status
          total
          totalTaxes
          availableLoyaltyRewards @include(if: $patronLoyalty) {
            balance
          }
          loyaltyEnrollmentOffer @include(if: $patronLoyalty) {
            points
          }
          orderOwner @include(if: $patronLoyalty) {
            id
            squareLoyaltyAccount {
              id
            }
          }
          squareLoyaltyRewards @include(if: $patronLoyalty) {
            id
            points
            status
            loyaltyCustomerId
            loyaltyAccount {
              id
            }
            rewardTier {
              id
              name
            }
          }
          ...PatronCheckoutLoyaltyRewards_order @include(if: $patronLoyalty)
          ...PatronCheckoutLoyaltyRewardsEnrollmentOffer_order
            @include(if: $patronLoyalty)
        }
      }
    }
    signUpWindow {
      cancelDeadlineAt
    }
    ...EventSignupSummaryPlayerList_signUp
    ...EventSignupSummaryTotal_signUp
    ...EventSignupSummaryPaymentSummary_signUp
      @arguments(patronLoyalty: $patronLoyalty)
    ...EventSignupSummaryReservation_signUp
      @arguments(patronLoyalty: $patronLoyalty)
    ...EventSignupSummaryActions_signUp
  }
`;
